import { css } from 'lit';

/**
 * Teknik's design tokens.
 *
 * The reason a site reads as one product rather than three is a small number of
 * decisions made once and then not re-litigated per component. Hardcoding
 * colours inside components is how this site ended up speaking indigo on the
 * homepage, teal on Apps, and gold on Store while wearing the same header.
 *
 * teknik.sh is the parent of lunako.app (violet #7c6cf6) and sumiko.app
 * (indigo #6358f9), both of which sit on the same blue-black. So the parent
 * deliberately does NOT: its ground is graphite with a green cast, and its one
 * accent is the teal that was already Teknik's own colour in the nav.
 *
 * The values are duplicated from styles/global.css on purpose. Custom
 * properties do inherit into shadow DOM, so global.css alone would work — but a
 * component that carries its own tokens still renders correctly if it is ever
 * used somewhere the global stylesheet isn't loaded.
 *
 * Import as the FIRST entry of `static styles`, so component rules can override
 * tokens but always have them defined:
 *
 *     static styles = [teknikTokens, teknikButtons, css`…`];
 */
export const teknikTokens = css`
  :host {
    /* Surfaces */
    --tk-bg: #0b0e0f;
    --tk-panel: #12171a;
    --tk-panel-2: #182125;
    --tk-border: #223138;
    --tk-border-strong: #2f434c;

    /* Text ramp — two steps below full strength, never inverted. */
    --tk-text: #e8eef0;
    --tk-muted: #a3b1b6;
    --tk-dim: #6e7f86;

    /* One accent. */
    --tk-accent: #4fc3ad;
    --tk-accent-hover: #63d4be;
    --tk-accent-soft: rgba(79, 195, 173, 0.12);
    --tk-accent-line: rgba(79, 195, 173, 0.42);

    /* Semantic only. Green here means "this process is up", not "brand". */
    --tk-ok: #3fb984;
    --tk-warn: #f0b429;
    --tk-down: #ef4444;

    --tk-r-control: 8px;
    --tk-r-card: 12px;
    --tk-max: 1080px;

    --font-family: 'Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    --font-display: 'Plex Cond', 'Plex Sans', -apple-system, 'Segoe UI', sans-serif;
    --font-mono: 'Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace;
  }
`;

/**
 * Three tiers, one language: solid for the single most important action on a
 * view, outlined for real-but-secondary actions, quiet for everything else.
 *
 * Tier is about importance, not availability. A working button styled to look
 * disabled costs more clicks than a missing button does.
 */
export const teknikButtons = css`
  .tk-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.45rem;
    padding: 0.6rem 1.05rem;
    border-radius: var(--tk-r-control);
    font-family: var(--font-family);
    font-size: 14px;
    font-weight: 500;
    line-height: 1.2;
    text-decoration: none;
    border: 1px solid transparent;
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
  }

  .tk-btn.primary {
    background: var(--tk-accent);
    color: #06110e;
    font-weight: 600;
  }
  .tk-btn.primary:hover {
    background: var(--tk-accent-hover);
  }

  .tk-btn.secondary {
    background: transparent;
    border-color: var(--tk-border-strong);
    color: var(--tk-text);
  }
  .tk-btn.secondary:hover {
    border-color: var(--tk-accent-line);
    color: var(--tk-accent);
  }

  .tk-btn.quiet {
    background: transparent;
    color: var(--tk-muted);
    padding-left: 0.4rem;
    padding-right: 0.4rem;
  }
  .tk-btn.quiet:hover {
    color: var(--tk-text);
  }
`;
